import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { UserStammdaten } from '../../entities/stammdaten.js';
import type { RegistrationStammdatenPlugin } from '../plugin-types.js';

export const mzsRegistrationStammdatenPlugin: RegistrationStammdatenPlugin = {
  id: 'registration::stammdaten::mzs',
  type: 'registration::stammdaten',
  disableCustomFormfields: true,
  createStammdaten: async ({ userID, institutionID, createInDB } = {}) => {
    const stammdaten = new MzsStammdaten({ userID, institutionID });
    if (createInDB) await stammdaten.create();
    return stammdaten;
  },
  getFields: () => {
    return Object.keys(MzsStammdaten.fields).filter(it => !['_id', '_class', 'userID', 'institutionID'].includes(it));
  },
  classType: () => MzsStammdaten,
};

/**
 * Pflichtfelder
 * Anrede
 * Hochschule (Freitext)
 * Fachbereich (Freitext)
 * Institution 
 * angestrebter Abschluss

  Optional
* Status Gruppe
* wie haben sie von unserem Angebot erfahren? (Mehrfachauswahl) (Flyer
Homepage
Info-Stand
Lehrveranstaltung/Lehrende
andere Studierende
sonstiges
Sarah Schmidt
10:31
Flyer/Plakate
Social Media)
* haben Sie schonmal an einem Workshop des Methodenzentrums teilgenommen?
 */

export enum MZSSalutationEnum {
  MZSFRAU = 'MZSFRAU',
  MZSHERR = 'MZSHERR',
  MZSSONSTIGES = 'MZSSONSTIGES',
}
registerEnum({ enum: MZSSalutationEnum, name: 'MZSSalutationEnum' });

// Institution
export enum MZSInstitutionEnum {
  MZS_BIOCHEMIE_CHEMIE_PHARMA = 'MZS_BIOCHEMIE_CHEMIE_PHARMA', //Biochemie, Chemie und Pharmazie
  MZS_BIO = 'MZS_BIO', //Biowissenschaften
  MZS_ERZIEHUNG = 'MZS_ERZIEHUNG', //Erziehungswissenschaf-ten
  MZSEVANG_THEOLOGIE = 'MZSEVANG_THEOLOGIE', //Evangelische Theologie
  MZSGEO = 'MZSGEO', //Geowissenschaften / Geographie
  MZSGESELLSCHAFT = 'MZSGESELLSCHAFT', //Gesellschaftswissenschaften
  MZSINFORMATIK = 'MZSINFORMATIK', //Informatik und Mathematik
  MZSKATH_THEOLOGIE = 'MZSKATH_THEOLOGIE', //Katholische Theologie
  MZSMEDIZIN = 'MZSMEDIZIN', //Medizin
  MZSNEU_PHILOLOGIE = 'MZSNEU_PHILOLOGIE', //Neuere Philologien
  MZSPHILOSOPHIE = 'MZSPHILOSOPHIE', //Philosophie und Geschichtswissenschaf-ten
  MZSPHYSIK = 'MZSPHYSIK', //Physik
  MZSPSYCHOLOGIE = 'MZSPSYCHOLOGIE', //Psychologie und Sportwissenschaften
  MZSRECHT = 'MZSRECHT', //Rechtswissenschaft
  MZSSPRACHEN = 'MZSSPRACHEN', //Sprach- und Kulturwissenschaft
  MZSWIRTSCHAFT = 'MZSWIRTSCHAFT', //Wirtschaftswissenschaften
  MZSOTHERS = 'MZSOTHERS', //Sonstige, mit extra Freitextfeld zur Eingabe
}
registerEnum({ enum: MZSInstitutionEnum, name: 'MZSInstitutionEnum' });

export enum MzsDesiredDegreeEnum {
  MZS_BACHELOR = 'MZS_BACHELOR',
  MZS_MASTER = 'MZS_MASTER',
  MZS_STAATSEXAMEN_LEHRAMT = 'MZS_STAATSEXAMEN_LEHRAMT',
  MZS_STAATSEXAMEN_MEDIZIN_PHARMAZIE = 'MZS_STAATSEXAMEN_MEDIZIN_PHARMAZIE',
  MZS_STAATSEXAMEN_JURA = 'MZS_STAATSEXAMEN_JURA',
  MZS_PROMOTION = 'MZS_PROMOTION',
  MZS_SONSTIGE = 'MZS_SONSTIGE',
}
registerEnum({ enum: MzsDesiredDegreeEnum, name: 'MzsDesiredDegreeEnum' });

export enum MzsStatusGroupEnum {
  MZS_STUDENT = 'MZS_STUDENT',
  MZS_TEACHER = 'MZS_TEACHER',
  // MZSEMPLOYEE_GOETHE = 'MZSEMPLOYEE_GOETHE',
  // MZSSONSTIGE = 'MZSSONSTIGE',
}
registerEnum({ enum: MzsStatusGroupEnum, name: 'MzsStatusGroupEnum' });

export enum MzsOfferEnum {
  MZS_FLYER = 'MZS_FLYER',
  MZS_HOMEPAGE = 'MZS_HOMEPAGE',
  MZS_SOCIAL_MEDIA = 'MZS_SOCIAL_MEDIA',
  MZS_INFO_STAND = 'MZS_INFO_STAND',
  MZS_COURSE = 'MZS_COURSE',
  MZS_OTHER_STUDENTS = 'MZS_OTHER_STUDENTS',
  MZS_OTHER_OPTION = 'MZS_OTHER_OPTION',
}
registerEnum({ enum: MzsOfferEnum, name: 'MzsOfferEnum' });

@Entity()
export class MzsStammdaten extends UserStammdaten {
  static override _class = 'MzsStammdaten';

  override getTranslationNamespace() {
    return 'Mzs';
  }

  // 1. Persönliche Angaben
  // Anrede: Frau / Herr / Sonstiges
  @validate(nonOptional())
  @Field(type => MZSSalutationEnum)
  salutation: Maybe<MZSSalutationEnum>;

  // Hochschule
  @validate(nonOptional())
  @Field(type => String)
  currentUni: Maybe<String>;

  //Institution
  @validate(nonOptional())
  @Field(type => MZSInstitutionEnum)
  institution: Maybe<MZSInstitutionEnum>;

  @Field(type => String)
  institutionOther: Maybe<string>;

  //angestrebter Abschluss
  @validate(nonOptional())
  @Field(type => MzsDesiredDegreeEnum)
  desiredDegree: Maybe<MzsDesiredDegreeEnum>;

  @Field(type => String)
  desiredDegreeOther: Maybe<string>;

  // 2. freiwillige Angaben
  //Status Gruppe
  @Field(type => MzsStatusGroupEnum)
  statusGroup: Maybe<MzsStatusGroupEnum>;

  @Field(type => String)
  statusGroupOther: Maybe<string>;

  //Wie haben Sie von unserem Angebot erfahren?
  @Field(type => MzsOfferEnum)
  hearAboutOffer: Maybe<MzsOfferEnum>;

  @Field(type => MzsOfferEnum)
  hearAboutOfferOther: Maybe<MzsOfferEnum>;

  //haben Sie schonmal an einem Workshop des Methodenzentrums teilgenommen?
  @Field(type => Boolean)
  participatedBefore: Maybe<Boolean>;
}
